import React, { useEffect, useState } from 'react';
import { Table, Button, Spin, Input, DatePicker, message,Row, Col } from 'antd';
import moment from 'moment';
import apiRequest from '../hooks/ApiRequest';
import Layouts from '../components/Layouts';

const { RangePicker } = DatePicker;

const ReinsuranceReport = () => {
    const [reportData, setReportData] = useState([]);
    const [filteredData, setFilteredData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchText, setSearchText] = useState('');
    const [startDate, setStartDate] = useState(null);
    const [endDate, setEndDate] = useState(null);

    useEffect(() => {
        fetchReinsuranceReport();
    }, []);

    const fetchReinsuranceReport = async () => {
        setLoading(true);
        try {
            const user = JSON.parse(localStorage.getItem('user'));
            const userId = user?.UserId;
            const role = user?.RoleName;

            let apiUrl = '';
            if (role === 'InsurerAdmin') {
                apiUrl = `/Policy/GetReinsuranceReportForInsurer/reinsurance-report/insurer/${userId}`;
            } else if (role === 'Broker') {
                apiUrl = `/Policy/GetReinsuranceReportForBroker/reinsurance-report/broker/${userId}`;
            } else if (role === 'Customer') {
                apiUrl = `/Policy/GetReinsuranceReportForCustomer/reinsurance-report/customer/${userId}`;
            } else {
                console.error('Unrecognized role:', role);
                setLoading(false);
                return;
            }

            const fullUrl = `${process.env.REACT_APP_BASE_URL}${apiUrl}`;
            const res = await apiRequest(fullUrl, 'GET');

            if (res.success && Array.isArray(res.data)) {
                setReportData(res.data);
                setFilteredData(res.data);
            } else {
                setReportData([]);
                setFilteredData([]);
            }
        } catch (error) {
            console.error('Failed to fetch reinsurance report:', error);
            setReportData([]);
            setFilteredData([]);
        } finally {
            setLoading(false);
        }
    };

    const handleFilter = () => {
        const search = searchText.toLowerCase();
        const filtered = reportData.filter((item) => {
            const policyMatch = item.policyNumber?.toLowerCase().includes(search);
            const nameMatch = item.customerName?.toLowerCase().includes(search);
            const emailMatch = item.customerEmail?.toLowerCase().includes(search);

            const startMatch = startDate
                ? moment(item.policyStartDate, 'DD-MM-YYYY HH:mm:ss').isSameOrAfter(startDate, 'day')
                : true;

            const endMatch = endDate
                ? moment(item.policyExpiryDate, 'DD-MM-YYYY HH:mm:ss').isSameOrBefore(endDate, 'day')
                : true;

            return (policyMatch || nameMatch || emailMatch) && startMatch && endMatch;
        });

        setFilteredData(filtered);
    };

    const clearFilters = () => {
        setSearchText('');
        setStartDate(null);
        setEndDate(null);
        setFilteredData(reportData);
    };

    const formatCurrency = (value) =>
        value != null
            ? Number(value).toLocaleString('en-IN', {
                style: 'currency',
                currency: 'INR',
            })
            : '-';

    const formatDate = (date) =>
        date ? moment(date, 'DD-MM-YYYY HH:mm:ss').format('DD-MM-YYYY') : '-';

    const exportToCSV = () => {
        const csvHeader = [
            'Policy Number',
            'Customer Name',
            'Customer Email',
            'Total Sum Insured',
            'Reinsurance Limit',
            'Policy Start Date',
            'Policy Expiry Date',
            'Product Name',
        ];

        const csvRows = filteredData.map((row) => [
            row.policyNumber,
            row.customerName,
            row.customerEmail,
            row.totalSumInsured,
            row.reinsuranceLimit,
            formatDate(row.policyStartDate),
            formatDate(row.policyExpiryDate),
            row.productName,
        ]);

        const csvContent =
            'data:text/csv;charset=utf-8,' +
            [csvHeader, ...csvRows].map((e) => e.join(',')).join('\n');

        const encodedUri = encodeURI(csvContent);
        const link = document.createElement('a');
        link.setAttribute('href', encodedUri);
        link.setAttribute('download', 'reinsurance_report.csv');
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const columns = [
        { key: '1', title: 'Policy Number', dataIndex: 'policyNumber' },
        { key: '2', title: 'Customer Name', dataIndex: 'customerName' },
        { key: '3', title: 'Customer Email', dataIndex: 'customerEmail' },
        {
            key: '4',
            title: 'Total Sum Insured',
            dataIndex: 'totalSumInsured',
            render: (sum) => formatCurrency(sum),
        },
        {
            key: '5',
            title: 'Reinsurance Limit',
            dataIndex: 'reinsuranceLimit',
            render: (limit) => formatCurrency(limit),
        },
        {
            key: '6',
            title: 'Start Date',
            dataIndex: 'policyStartDate',
            render: (date) => formatDate(date),
        },
        {
            key: '7',
            title: 'Expiry Date',
            dataIndex: 'policyExpiryDate',
            render: (date) => formatDate(date),
        },
        {
            key: '8',
            title: 'Product Name',
            dataIndex: 'productName',
        },
    ];

    return (
        <Layouts title="Reinsurance Report" classname="dashboard">
            <Spin className="loader-icon-fixed" spinning={loading} size="large" />

            <div className="dash-bg-white">
                <Row gutter={[16, 16]}>
                    <Col xs={12} sm={24} md={24} lg={12}>
                        <div className="section-top-heading">
                            <h3 style={{ color: '#0C1362', fontWeight: 600, fontSize: '20px' }}>
                                Reinsurance Report
                            </h3>
                        </div>
                    </Col>
                </Row>
            </div>

            {/* Filters */}
            <div className="dash-bg-white">
                <div className="m-b-20">
                    <Row gutter={[24, 24]}>
                        {/* Left side: filters */}
                        <Col xs={24} sm={12} md={18} lg={20} className="custom-statcards">
                            <Input
                                placeholder="Policy Number/Name/Email"
                                value={searchText}
                                onChange={(e) => setSearchText(e.target.value)}
                                style={{ width: 300 }}
                            />
                            &nbsp;&nbsp;&nbsp;
                            <DatePicker
                                placeholder="Start Date"
                                value={startDate}
                                onChange={(date) => setStartDate(date)}
                            />
                            &nbsp;&nbsp;&nbsp;
                            <DatePicker
                                placeholder="Expiry Date"
                                value={endDate}
                                onChange={(date) => setEndDate(date)}
                            />
                            &nbsp;&nbsp;&nbsp;
                            <Button type="primary" onClick={handleFilter}>
                                Search
                            </Button>
                            &nbsp;
                            <Button onClick={clearFilters}>Clear</Button>
                        </Col>

                        {/* Right side: export button */}
                        <Col xs={24} sm={12} md={6} lg={4} className="custom-statcards">
                            {filteredData.length > 0 && (
                                <div className="text-right-export">
                                    <Button
                                        style={{
                                            color: '#5cb85c',
                                            borderColor: '#5cb85c',
                                        }}
                                        onClick={exportToCSV}
                                    >
                                        Export
                                    </Button>
                                </div>
                            )}
                        </Col>
                    </Row>
                </div>
            </div>

            {/* Table */}
            <div className="table-grid-bx">
                <Table
                    className="table-scroll-auto"
                    columns={columns}
                    dataSource={filteredData}
                    loading={loading}
                    rowKey="policyId"
                    pagination={{ pageSize: 10 }}
                />
            </div>
        </Layouts>
    );

};

export default ReinsuranceReport;
